import { useEffect, useRef, useState } from "react";
import PolicyModal from "./PolicyModal";
import GuaranteeSeal from "./GuaranteeSeal";
import { asset } from "../../assetUrl";
import "./Template4.css";
import { BRAND_DEFAULT_SVG } from "../../brandDefaultLogo";

/* Default brand logo (shown until the creator sets their own) + default course
   cover image. Both are overridden by data when the creator uploads one. */
// Course-landing pages are light-only (no dark: classes anywhere in this
// folder), so the light placeholder is the right one. The old default was
// FlexiLogo.svg — OUR mark, on a CUSTOMER's sales page, which contradicts the
// rule stated on BrandLogo — and it was a 100 KB "SVG" that turned out to be a
// single <rect> filled with a base64 PNG, so it could not be recoloured either.
const DEFAULT_LOGO = BRAND_DEFAULT_SVG;
const DEFAULT_COURSE_IMAGE = asset("/images/course-default.svg");

/* ──────────────────────────────────────────────────────────────────────────
   Template 4 — "Gold / Obsidian" luxury
   Pixel-exact JSX port of resources/views/fe/courselanding4.blade.php.
   All visual CSS lives in Template4.css scoped under `.clp4-root` (fully
   var-driven via the wrapper's --cl-* vars). Bootstrap (grid + icons) is not
   loaded in this app, so the grid/utilities are reproduced in the CSS and
   every `bi bi-*` icon is an inline SVG below.
   ────────────────────────────────────────────────────────────────────────── */

/* ── Inline SVG icons (Bootstrap Icons equivalents) ───────────────────────── */
const svg = (p) => ({ viewBox: "0 0 16 16", fill: "currentColor", width: "1em", height: "1em", ...p });
const Icon = {
  mortarboardFill: (p) => (
    <svg {...svg(p)}>
      <path d="M8.211 2.047a.5.5 0 0 0-.422 0l-7.5 3.5a.5.5 0 0 0 .025.917l7.5 3a.5.5 0 0 0 .372 0L14 7.14V13a1 1 0 0 0-1 1v2h3v-2a1 1 0 0 0-1-1V6.739l.686-.275a.5.5 0 0 0 .025-.917l-7.5-3.5Z" />
      <path d="M4.176 9.032a.5.5 0 0 0-.656.327l-.5 1.7a.5.5 0 0 0 .294.605l4.5 1.8a.5.5 0 0 0 .372 0l4.5-1.8a.5.5 0 0 0 .294-.605l-.5-1.7a.5.5 0 0 0-.656-.327L8 10.466 4.176 9.032Z" />
    </svg>
  ),
  playCircle: (p) => (
    <svg {...svg(p)}>
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z" />
    </svg>
  ),
  playFill: (p) => (
    <svg {...svg(p)}>
      <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z" />
    </svg>
  ),
  book: (p) => (
    <svg {...svg(p)}>
      <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783z" />
    </svg>
  ),
  checkLg: (p) => (
    <svg {...svg(p)}>
      <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z" />
    </svg>
  ),
  dot: (p) => (
    <svg {...svg(p)}>
      <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
    </svg>
  ),
  chevronDown: (p) => (
    <svg {...svg(p)}>
      <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
    </svg>
  ),
  fileEarmarkText: (p) => (
    <svg {...svg(p)}>
      <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z" />
      <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
    </svg>
  ),
  shieldCheck: (p) => (
    <svg {...svg(p)}>
      <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.30 8.531 1.1 8 1.1c-.531 0-1.552.2-2.662.49zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.379 10.485-.004 7.167.592 2.692A1.54 1.54 0 0 1 1.636 1.43a62.468 62.468 0 0 1 2.887-.87z" />
      <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
    </svg>
  ),
  plusLg: (p) => (
    <svg {...svg(p)}>
      <path fillRule="evenodd" d="M8 0a1 1 0 0 1 1 1v6h6a1 1 0 1 1 0 2H9v6a1 1 0 1 1-2 0V9H1a1 1 0 0 1 0-2h6V1a1 1 0 0 1 1-1Z" />
    </svg>
  ),
  twitterX: (p) => (
    <svg {...svg(p)}>
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
    </svg>
  ),
  youtube: (p) => (
    <svg {...svg(p)}>
      <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
    </svg>
  ),
  linkedin: (p) => (
    <svg {...svg(p)}>
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
    </svg>
  ),
  instagram: (p) => (
    <svg {...svg(p)}>
      <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
    </svg>
  ),
  facebook: (p) => (
    <svg {...svg(p)}>
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
    </svg>
  ),
};

export default function Template4({ data }) {
  const {
    course = {},
    logo = null,
    header = {},
    skill = {},
    course_include = {},
    prerequisites = {},
    testimonial = {},
    money_back = {},
    faq = {},
    social = {},
    policies = {},
    labels = {},
    video = {},
    video_embed = null,
    modules = [],
    lessonsByModule = {},
    moduleCount = 0,
    lessonCount = 0,
    instructor = null,
    rating = 0,
    enrollText = "Enroll now",
    enrollLink = "#",
  } = data || {};

  const lbl = (k, d) => {
    const v = (labels && labels[k]) != null ? String(labels[k]).trim() : "";
    return v !== "" ? labels[k] : d;
  };
  const courseName = course.name || course.title || "";
  const heroTitle = (header.headline || "").trim() || courseName;
  const heroSub = (header.sub_headline || "").trim() || course.short_description || "";
  // Nav brand logo: creator's logo when set, else the default brand logo.
  const logoImg = String(logo || "").trim() !== "" ? logo : DEFAULT_LOGO;
  // Course cover (hero visual): uploaded image, else default.
  const coverImg = course.funnel_page_image || DEFAULT_COURSE_IMAGE;
  // "Course includes" toggle gates the curriculum section (default ON).
  const showCurriculum =
    course_include.status === undefined ? true : !!course_include.status;
  const lessonsCountFor = (cid) => (lessonsByModule[String(cid)] || []).length;
  const words = (s) => String(s || "").trim().split(/\s+/).filter(Boolean);
  const Stars = ({ n }) => <>{[1, 2, 3, 4, 5].map((i) => (i <= Math.round(n) ? "★" : "☆")).join("")}</>;

  // hero title -> 3 lines
  const hw = words(heroTitle);
  const line1 = hw[0] || "";
  const line2 = hw[1] || "";
  const line3 = hw.slice(2).join(" ");

  // section heading splits
  const skh = skill.heading || "What you'll learn";
  const skw = words(skh);
  const skillHeadTail = skw.length > 3;

  const th = testimonial.heading || "What students are saying";
  const tw = words(th);
  const tLast = tw.length ? tw[tw.length - 1] : "";
  const tHead = tw.slice(0, -1).join(" ");

  const fh = faq.heading || "Common questions";
  const fw = words(fh);
  const fLast = fw.length ? fw[fw.length - 1] : "";
  const fHead = fw.slice(0, -1).join(" ");

  // guarantee
  const mbStatus = money_back.status != null ? money_back.status : 1;
  const mbHeading = (money_back.heading || "").trim() || "30-Day Risk-Free Money-Back Guarantee";
  const mbDesc =
    (money_back.description || "").trim() ||
    "We believe in the value of this course and are confident it will help you achieve your goals. That’s why we offer a full 30-day risk-free money-back guarantee. <br><br> Go through the content, explore the lessons, and put what you learn into action. If at any point within the first 30 days you feel the course isn’t right for you or doesn’t meet your expectations, simply reach out to us.<br><br> We’ll process your refund promptly—no questions asked.<br><br> There’s absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.";
  const gw = words(mbHeading);

  // CTA words
  const cw = words(heroTitle);
  const cFirst = cw.slice(0, 2).join(" ");
  const cHigh = cw[2] || "";
  const cLast = cw.slice(3).join(" ");

  const showHeroRight = header.status == null || header.status;

  /* ── interactivity ── */
  const rootRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoOn, setVideoOn] = useState(false);
  const [openMods, setOpenMods] = useState(() => new Set([0])); // first module open
  const [openFaqs, setOpenFaqs] = useState(() => new Set());
  // Footer policy popup: "privacy" | "terms" | null
  const [openPolicy, setOpenPolicy] = useState(null);
  const hasPrivacy = String(policies?.privacy || "").trim() !== "";
  const hasTerms = String(policies?.terms || "").trim() !== "";

  useEffect(() => {
    const onScroll = () => {
      setStuck(window.scrollY > 30);
      const h = document.body.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((x) => {
          if (x.isIntersecting) {
            x.target.classList.add("on");
            io.unobserve(x.target);
          }
        });
      },
      { threshold: 0.08 },
    );
    root.querySelectorAll(".rv").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const toggleSet = (setter) => (i) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  const toggleMod = toggleSet(setOpenMods);
  const toggleFaq = toggleSet(setOpenFaqs);

  const scrollToId = (e, id) => {
    const t = document.getElementById(id);
    if (t) {
      e.preventDefault();
      t.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMobOpen(false);
  };

  const videoType = video.video_type || "youtube";
  const videoSrc = video_embed
    ? `${video_embed}${video_embed.includes("?") ? "&" : "?"}enablejsapi=1&autoplay=1`
    : "";

  return (
    <div className="clp4-root" ref={rootRef}>
      {/* progress bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "2px",
          width: `${progress}%`,
          background: "linear-gradient(90deg,var(--gd-d),var(--gd))",
          zIndex: 9999,
          transition: "width .1s",
          pointerEvents: "none",
        }}
      />

      {/* ▓ NAV ▓ */}
      <nav className={`nav${stuck ? " stuck" : ""}`}>
        <div className="w">
          <div className="nav-in">
            <a href="#home" className="logo" onClick={(e) => scrollToId(e, "home")}>
              <img src={logoImg} alt="Logo" />
            </a>
            <div className="nav-links">
              {moduleCount && showCurriculum ? <a href="#curriculum" className="nl" onClick={(e) => scrollToId(e, "curriculum")}>{lbl("curriculum", "Curriculum")}</a> : null}
              {instructor && instructor.name ? <a href="#instructor" className="nl" onClick={(e) => scrollToId(e, "instructor")}>{lbl("instructor", "Instructor")}</a> : null}
              {testimonial.status && (testimonial.list || []).length ? <a href="#reviews" className="nl" onClick={(e) => scrollToId(e, "reviews")}>{lbl("reviews", "Reviews")}</a> : null}
              {faq.status && (faq.list || []).length ? <a href="#faq" className="nl" onClick={(e) => scrollToId(e, "faq")}>FAQ</a> : null}
            </div>
            <a href={enrollLink} className="nav-btn">
              <Icon.mortarboardFill className="me-2" />{enrollText}
            </a>
            <button className="ham" onClick={() => setMobOpen((o) => !o)} aria-label="Menu"><span></span><span></span><span></span></button>
          </div>
          <div className={`mob${mobOpen ? " open" : ""}`}>
            {moduleCount && showCurriculum ? <a href="#curriculum" className="nl" onClick={(e) => scrollToId(e, "curriculum")}>{lbl("curriculum", "Curriculum")}</a> : null}
            {instructor && instructor.name ? <a href="#instructor" className="nl" onClick={(e) => scrollToId(e, "instructor")}>{lbl("instructor", "Instructor")}</a> : null}
            {testimonial.status && (testimonial.list || []).length ? <a href="#reviews" className="nl" onClick={(e) => scrollToId(e, "reviews")}>{lbl("reviews", "Reviews")}</a> : null}
            {faq.status && (faq.list || []).length ? <a href="#faq" className="nl" onClick={(e) => scrollToId(e, "faq")}>FAQ</a> : null}
            <a href={enrollLink} className="nav-btn" onClick={() => setMobOpen(false)}>
              <Icon.mortarboardFill className="me-2" />{enrollText}
            </a>
          </div>
        </div>
      </nav>

      {/* ▓ HERO ▓ */}
      <section
        className="hero"
        id="home"
        style={header.header_bg_image ? { backgroundImage: `url('${header.header_bg_image}')`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
      >
        <div className="hero-tex"></div>
        <div className="hero-circle"></div>
        <div className="hero-circle2"></div>
        <div className="w" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div className="hero-body">
            {/* Left */}
            <div className="hero-l">
              <h1 className="hero-title fu a1">
                {line1}<br />
                {line2 ? (<><em>{line2}</em><br /></>) : null}
                {line3 ? <span className="serif-light">{line3}</span> : null}
              </h1>
              {heroSub ? <p className="hero-subtitle fu a2">{heroSub}</p> : null}
              <div className="hero-actions fu a3">
                <a href={enrollLink} className="btn-gd">
                  <Icon.mortarboardFill /> {enrollText}
                </a>
                {moduleCount && showCurriculum ? (
                  <button className="btn-out" onClick={(e) => scrollToId(e, "curriculum")}>
                    <Icon.playCircle /> {lbl("view_curriculum", "Preview Course")}
                  </button>
                ) : null}
              </div>
              {rating > 0 ? (
                <div className="hero-proof fu a4">
                  <div className="pf-item proof-rating">
                    <span className="stars"><Stars n={rating} /></span>
                    &nbsp;<strong>{Number(rating).toFixed(1)}</strong> / 5 {lbl("rating", "Rating")}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Right */}
            {showHeroRight ? (
              <div className="hero-r fu a3">
                <div className="hero-frame position-relative">
                  {video_embed ? (
                    videoOn ? (
                      <div id="video_ifr_box4">
                        {videoType === "other" ? (
                          <video style={{ width: "100%" }} controls autoPlay>
                            <source src={video_embed} type="video/mp4" />
                          </video>
                        ) : (
                          <iframe title="promo" style={{ width: "100%", height: "300px" }} src={videoSrc} frameBorder="0" allowFullScreen />
                        )}
                      </div>
                    ) : (
                      <>
                        <div onClick={() => setVideoOn(true)} className="video_thumb">
                          <img src={video.image || coverImg} className="hero-img w-100" alt="" />
                        </div>
                        <div onClick={() => setVideoOn(true)} className="play-btn position-absolute top-50 start-50 translate-middle">
                          <Icon.playFill />
                        </div>
                      </>
                    )
                  ) : (
                    <img src={coverImg} className="hero-img w-100" alt="" />
                  )}
                </div>
                {modules.length ? (
                  <div className="hero-enroll">
                    <div className="he-left">
                      <div className="he-feats">
                        {modules.slice(0, 2).map((m) => (
                          <div className="he-feat" key={m.course_id}>
                            <Icon.book /> {m.course_name} ({lessonsCountFor(m.course_id)} {lbl("lessons", "lessons")})
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* ▓ WHAT YOU'LL LEARN ▓ */}
      {skill.status && (skill.list || []).length ? (
        <section className="gain" id="gain">
          <div className="w">
            <div className="rv">
              <div className="lbl lbl-gold"><span className="gold-line" style={{ margin: 0 }}></span>{lbl("what_youll_learn", "What You'll Learn.")}</div>
              <h2 style={{ fontSize: "clamp(2rem,3.5vw,3rem)", color: "var(--iv)", marginTop: ".6rem", maxWidth: "520px" }}>
                {skillHeadTail ? (
                  <>
                    {skw.slice(0, skw.length - 3).join(" ")}{" "}
                    <em style={{ color: "var(--gd)" }}>{skw.slice(-3).join(" ")}</em>
                  </>
                ) : (
                  <em style={{ color: "var(--gd)" }}>{skh}</em>
                )}
              </h2>
            </div>
            <div className="gain-grid">
              {(skill.list || []).map((s, i) => (
                <div className={`gain-card rv d${i + 1}`} key={i}>
                  <div className="gain-num">{String(i + 1).padStart(2, "0")}</div>
                  <p className="gain-desc">{s.pts || ""}</p>
                </div>
              ))}
            </div>
            {skill.long_desc ? (
              <p className="rv" style={{ color: "var(--mx)", marginTop: "2rem", maxWidth: "760px", lineHeight: 1.8 }}
                dangerouslySetInnerHTML={{ __html: String(skill.long_desc).replace(/\n/g, "<br>") }} />
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ▓ COURSE INCLUDES ▓ */}
      {course_include.status && (course_include.list || []).length ? (
        <section className="gain" id="includes">
          <div className="w">
            <div className="rv">
              <div className="lbl lbl-gold"><span className="gold-line" style={{ margin: 0 }}></span>{lbl("what_inside_course", "What's inside")}</div>
              <h2 style={{ fontSize: "clamp(2rem,3.5vw,3rem)", color: "var(--iv)", marginTop: ".6rem", maxWidth: "520px" }}>
                <em style={{ color: "var(--gd)" }}>{course_include.heading || "This course includes"}</em>
              </h2>
            </div>
            <div className="gain-grid">
              {(course_include.list || []).map((c, i) => (
                <div className={`gain-card rv d${i + 1}`} key={i}>
                  <div className="gain-num"><Icon.checkLg /></div>
                  <p className="gain-desc">{c.pts || ""}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ PREREQUISITES ▓ */}
      {prerequisites.status && (prerequisites.list || []).length ? (
        <section className="gain" id="requirements">
          <div className="w">
            <div className="rv">
              <div className="lbl lbl-gold"><span className="gold-line" style={{ margin: 0 }}></span>{lbl("prerequisites", "Prerequisites")}</div>
              <h2 style={{ fontSize: "clamp(2rem,3.5vw,3rem)", color: "var(--iv)", marginTop: ".6rem", maxWidth: "520px" }}>
                <em style={{ color: "var(--gd)" }}>{prerequisites.heading || "Before you start"}</em>
              </h2>
            </div>
            <div className="gain-grid">
              {(prerequisites.list || []).map((p, i) => (
                <div className={`gain-card rv d${i + 1}`} key={i}>
                  <div className="gain-num"><Icon.dot /></div>
                  <p className="gain-desc">{p.pts || ""}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ CURRICULUM ▓ */}
      {moduleCount && showCurriculum ? (
        <section className="curriculum" id="curriculum">
          <div className="w">
            <div className="cur-header rv">
              <div>
                <div className="lbl lbl-gold"><span className="gold-line" style={{ margin: 0 }}></span>{lbl("curriculum", "Curriculum")}</div>
                <h2 style={{ fontSize: "clamp(2rem,3.5vw,3rem)", color: "var(--iv)", marginTop: ".6rem" }}>{lbl("inside_course", "What's inside the course")}</h2>
              </div>
              <div className="cur-badge">{moduleCount} {lbl("modules", "modules")} · {lessonCount} {lbl("lessons", "lessons")}</div>
            </div>
            <div style={{ maxWidth: "680px" }}>
              {modules.map((m, i) => {
                const mls = lessonsByModule[String(m.course_id)] || [];
                const open = openMods.has(i);
                return (
                  <div className={`mod d${i + 1}`} key={m.course_id}>
                    <button className={`mod-btn${open ? " active" : ""}`} onClick={() => toggleMod(i)} aria-expanded={open}>
                      <div className="mod-n">{i + 1}</div>
                      <div className="mod-t">{m.course_name}</div>
                      <div className="mod-m">{mls.length} {lbl("lessons", "Lessons")}</div>
                      <Icon.chevronDown className="mod-chev" />
                    </button>
                    <div style={{ display: open ? "block" : "none" }}>
                      {mls.length ? (
                        mls.map((child) => (
                          <div className="lesson" key={child.lesson_id}>
                            {Number(child.lesson_type || 0) === 0 ? <Icon.fileEarmarkText className="lesson-ic" /> : <Icon.playCircle className="lesson-ic" />}
                            <span className="lesson-nm">{String(child.lesson_name || "Lesson").slice(0, 100)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-3" style={{ color: "var(--mx)" }}>{lbl("no_lesson_found", "No lessons found")}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ INSTRUCTOR ▓ */}
      {instructor && instructor.name ? (
        <section className="instructor" id="instructor">
          <div className="w">
            <div className="rv" style={{ marginBottom: "2rem" }}>
              <div className="lbl lbl-gold"><span className="gold-line" style={{ margin: 0 }}></span>{lbl("instructor", "Instructor")}</div>
            </div>
            <div className="instr-card rv d1">
              <div className="instr-img-col">
                {instructor.profile_image ? (
                  <img src={instructor.profile_image} alt="Instructor" />
                ) : (
                  <div className="instr-img-ph">
                    {instructor.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}
                  </div>
                )}
              </div>
              <div className="instr-body">
                <div className="instr-lbl">{lbl("meet_instructor", "Meet the Instructor")}</div>
                <h2 className="instr-name">{instructor.name}</h2>
                {instructor.location ? <div className="instr-role">{instructor.location}</div> : null}
                <p className="instr-bio">{instructor.short_description || ""}</p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ REVIEWS ▓ */}
      {testimonial.status && (testimonial.list || []).length ? (
        <section className="reviews" id="reviews">
          <div className="w">
            <div className="rv" style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "1.5rem", flexWrap: "wrap", marginBottom: "2.75rem" }}>
              <div>
                <div className="lbl lbl-gold"><span className="gold-line" style={{ margin: 0 }}></span>{lbl("testimonials", "Testimonials")}</div>
                <h2 style={{ fontSize: "clamp(2rem,3.5vw,3rem)", color: "var(--iv)", marginTop: ".6rem" }}>
                  {tHead}{tLast ? (<> <em style={{ color: "var(--gd)" }}>{tLast}</em></>) : null}
                </h2>
              </div>
            </div>
            <div className="rv-layout">
              {(testimonial.list || []).map((t, i) => (
                <div className={`rv-card rv d${(i % 3) + 1}`} key={i}>
                  <span className="rv-q">"</span>
                  <p className="rv-text">{t.review || ""}</p>
                  <div className="rv-line"></div>
                  <div className="rv-foot">
                    <div className="rv-av" style={{ background: "var(--ob3)", color: "var(--gd)" }}>{t.name ? t.name.slice(0, 2).toUpperCase() : "NA"}</div>
                    <div>
                      <div className="rv-stars"><Stars n={Number(t.rating || 5)} /></div>
                      <div className="rv-name">{t.name || ""}</div>
                      <div className="rv-role">{t.designation || ""}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ GUARANTEE ▓ */}
      {mbStatus == 1 ? (
        <section className="guarantee" style={money_back.money_back_bg_image ? { backgroundImage: `url('${money_back.money_back_bg_image}')`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
          <div className="w">
            <div className="row align-items-center guar-row">
              <div className="col-lg-6">
                <div className="guar-inner text-lg-start text-center">
                  <h2 className="guar-title">
                    {gw.map((word, index) => (index === 1 ? <em key={index}>{word} </em> : <span key={index}>{word} </span>))}
                  </h2>
                  <p className="guar-text" dangerouslySetInnerHTML={{ __html: mbDesc }} />
                </div>
              </div>
              <div className="col-lg-6 text-center">
                {money_back.money_back_image ? (
                  <img src={money_back.money_back_image} alt="Guarantee Image" className="img-fluid rounded guar-img" />
                ) : (
                  <div className="guar-img"><GuaranteeSeal /></div>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ FAQ ▓ */}
      {faq.status && (faq.list || []).length ? (
        <section className="faq" id="faq">
          <div className="w">
            <div className="rv" style={{ textAlign: "center", marginBottom: 0 }}>
              <div className="lbl lbl-gold" style={{ justifyContent: "center" }}><span className="gold-line" style={{ margin: 0 }}></span>{lbl("faq", "FAQ")}</div>
              <h2 style={{ fontSize: "clamp(2rem,3.5vw,3rem)", color: "var(--iv)", marginTop: ".6rem" }}>
                {fHead} <em style={{ color: "var(--gd)" }}>{fLast}</em>
              </h2>
            </div>
            <div className="faq-in">
              {(faq.list || []).map((f, i) => {
                const open = openFaqs.has(i);
                return (
                  <div className={`fq d${(i % 3) + 1}`} key={i}>
                    <button className="fq-btn" onClick={() => toggleFaq(i)} aria-expanded={open}>
                      {f.question || ""}
                      <Icon.plusLg className="fq-ic" />
                    </button>
                    <div className="fq-body" style={{ display: open ? "block" : "none" }}>{f.answer || ""}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* ▓ CTA ▓ */}
      <section className="cta">
        <div className="cta-top">
          <div className="w">
            <div className="cta-inner rv">
              <h2 className="cta-title">
                {cFirst}{cHigh ? (<> <em>{cHigh}</em></>) : null}
                <br />
                {cLast}
              </h2>
              {heroSub ? <p className="cta-sub">{heroSub}</p> : null}
              <a href={enrollLink} className="btn-gd">
                <Icon.mortarboardFill /> {enrollText}
              </a>
              <div className="cta-note">
                <Icon.shieldCheck />
                <span>{lbl("money_back_guarantee", "30-Day Money-Back Guarantee.") + " " + lbl("not_satisfied", "Not satisfied? Full refund, no questions asked")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ▓ FOOTER ▓ */}
      <footer className="footer">
        <div className="w">
          <div className="footer-row d-flex justify-content-between align-items-center flex-wrap">
            <div className="socials d-flex align-items-center gap-3">
              {social.x_url ? <a href={social.x_url} className="soc" target="_blank" rel="noopener noreferrer"><Icon.twitterX /></a> : null}
              {social.youtube_url ? <a href={social.youtube_url} className="soc" target="_blank" rel="noopener noreferrer"><Icon.youtube /></a> : null}
              {social.linkedin_url ? <a href={social.linkedin_url} className="soc" target="_blank" rel="noopener noreferrer"><Icon.linkedin /></a> : null}
              {social.instagram_url ? <a href={social.instagram_url} className="soc" target="_blank" rel="noopener noreferrer"><Icon.instagram /></a> : null}
              {social.facebook_url ? <a href={social.facebook_url} className="soc" target="_blank" rel="noopener noreferrer"><Icon.facebook /></a> : null}
            </div>
            {hasPrivacy || hasTerms ? (
              <div className="foot-links">
                {hasPrivacy ? (
                  <button type="button" onClick={() => setOpenPolicy("privacy")}>
                    {policies.privacy_subject || "Privacy Policy"}
                  </button>
                ) : null}
                {hasTerms ? (
                  <button type="button" onClick={() => setOpenPolicy("terms")}>
                    {policies.terms_subject || "Terms"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </footer>

      {/* ▓ STICKY BAR ▓ */}
      <div className="sticky-bar">
        <div className="sticky-in">
          <div className="sticky-price">{courseName.slice(0, 28)}</div>
          <a href={enrollLink} className="btn-sticky">
            <Icon.mortarboardFill className="me-2" />{enrollText}
          </a>
        </div>
      </div>

      <PolicyModal
        open={openPolicy}
        policies={policies}
        onClose={() => setOpenPolicy(null)}
      />
    </div>
  );
}
